/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Eye, FileText, Calendar, MapPin, ChevronLeft, ChevronRight, Filter, X, Plus, Edit, Trash2, Clock } from "lucide-react";
import apiService from "@/services/apiService";
import bgImage from "@/assets/favicon.png";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { CriminalFormPopup, type GoondaActCaseForm } from "@/components/CriminalFormPopup";
import useErrorStore from "@/store/confirmationStore";

interface CaseFilters {
  district: string[];
  policeStation: string[];
  year: string[];
  actSection: string[];
  currentStage: string[];
  status: string[];
  caseNumberSearch: string;
  accusedNameSearch: string;
  createdDateFrom: string;
  createdDateTo: string;
}

const CASE_STAGES = [
  { key: "SHO_REPORT", label: "SHO Submitted Report" },
  { key: "CO_SIGN", label: "CO Approval" },
  { key: "SDM_SIGN", label: "SDM Approval" },
  { key: "SP_PERMISSION", label: "SP Permission" },
  { key: "GD_ENTRY", label: "GD Entry" },
  { key: "DM_NOTICE_ISSUED", label: "DM Notice" },
  { key: "UNDER_HEARING", label: "Under Hearing" },
  { key: "FINAL_ORDER", label: "Final Order" },
];

export default function GoondaActCases() {
  const showError = useErrorStore((s) => s.showError);
  const [cases, setCases] = useState<GoondaActCaseForm[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingCase, setViewingCase] = useState<GoondaActCaseForm | null>(null);
  const [editingCase, setEditingCase] = useState<GoondaActCaseForm | null>(null);
  const [deleteCase, setDeleteCase] = useState<GoondaActCaseForm | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [mode, setMode] = useState("view");

  const [filters, setFilters] = useState<CaseFilters>({
    district: [],
    policeStation: [],
    year: [],
    actSection: [],
    currentStage: [],
    status: [],
    caseNumberSearch: "",
    accusedNameSearch: "",
    createdDateFrom: "",
    createdDateTo: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    districts: [] as string[],
    policeStations: [] as string[],
    years: [] as string[],
    actSections: [] as string[],
  });

  const handleViewCase = (caseData: GoondaActCaseForm) => {
    setMode("view");
    setViewingCase(caseData);
    setIsFormOpen(true);
  };

  const handleEditCase = (caseData: GoondaActCaseForm) => {
    setMode("edit");
    setEditingCase(caseData);
    setIsFormOpen(true);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
    fetchCases(itemsPerPage, value);
  };

  const confirmDelete = async () => {
    if (deleteCase && deleteCase.id) {
      try {
        await apiService.delete(`goonda-act-case/${deleteCase.id}`);
        toast.success("Case deleted successfully");
        setCases(cases.filter((c) => c.id !== deleteCase.id));
        setDeleteCase(null);
        fetchCases();
      } catch (error) {
        console.log(error)
        showError("Failed to delete case");
      }
    }
  };

  const getCurrentStage = (stageHistory?: any[]) => {
    if (!stageHistory || stageHistory.length === 0) return "Pending";
    
    const completedStages = stageHistory
      .filter((h) => h.actionDate)
      .map((h) => h.stage);
    
    const lastCompletedStage = CASE_STAGES.find((stage) => 
      completedStages.includes(stage.key)
    );
    
    if (!lastCompletedStage) return "Pending";
    
    const lastIndex = CASE_STAGES.findIndex((s) => s.key === lastCompletedStage.key);
    if (lastIndex === CASE_STAGES.length - 1) return "Completed";
    
    return CASE_STAGES[lastIndex + 1].label;
  };

  const getStageBadge = (stageHistory?: any[]) => {
    const stage = getCurrentStage(stageHistory);
    const colorMap: Record<string, string> = {
      "Pending": "bg-gray-100 text-gray-800",
      "CO Approval": "bg-blue-100 text-blue-800",
      "SDM Approval": "bg-indigo-100 text-indigo-800",
      "SP Permission": "bg-purple-100 text-purple-800",
      "GD Entry": "bg-cyan-100 text-cyan-800",
      "DM Notice": "bg-orange-100 text-orange-800",
      "Under Hearing": "bg-yellow-100 text-yellow-800",
      "Final Order": "bg-green-100 text-green-800",
      "Completed": "bg-green-100 text-green-800",
    };
    return <Badge className={`${colorMap[stage] || "bg-gray-100 text-gray-800"} hover:${colorMap[stage] || "bg-gray-100"}`}>{stage}</Badge>;
  };

  const fetchFilterOptions = async () => {
    const districts = [...new Set(cases.map(c => c.district).filter(Boolean))];
    const policeStations = [...new Set(cases.map(c => c.policeStation).filter(Boolean))];
    const years = [...new Set(cases.map(c => c.year).filter(Boolean))];
    const actSections = [...new Set(cases.map(c => c.actSection).filter(Boolean))];

    setFilterOptions({
      districts,
      policeStations,
      years,
      actSections,
    });
  };

  const fetchCases = async (limit = 10, page = 1) => {
    const queryParams: any = { limit, page };

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        queryParams[key] = value.join(",");
      } else if (typeof value === "string" && value.trim()) {
        queryParams[key] = value;
      }
    });

    if (searchTerm.trim()) {
      queryParams.search = searchTerm;
    }

    try {
      const jsonResponse: any = await apiService.get(`goonda-act-case`, queryParams, {});
      setCases(jsonResponse.data);
      setTotalPages(jsonResponse.pagination?.totalPages || 1);
      setStartIndex(jsonResponse.pagination?.limit * (jsonResponse.pagination?.page - 1) || 0);
      setEndIndex(jsonResponse.pagination?.limit * jsonResponse.pagination?.page || jsonResponse.data.length);
      setTotal(jsonResponse.pagination?.total || jsonResponse.data.length);
    } catch (error) {
      console.error("Error fetching cases:", error);
    }
  };

  const handleFilterChange = (filterKey: keyof CaseFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const handleMultiSelectFilter = (filterKey: keyof CaseFilters, value: string) => {
    setFilters((prev) => {
      const currentValues = prev[filterKey] as string[];
      const newValues = currentValues.includes(value) 
        ? currentValues.filter((v) => v !== value) 
        : [...currentValues, value];
      return {
        ...prev,
        [filterKey]: newValues,
      };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      district: [],
      policeStation: [],
      year: [],
      actSection: [],
      currentStage: [],
      status: [],
      caseNumberSearch: "",
      accusedNameSearch: "",
      createdDateFrom: "",
      createdDateTo: "",
    });
    setSearchTerm("");
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchCases(itemsPerPage, 1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([, value]) => {
      if (Array.isArray(value) && value.length > 0) count++;
      else if (typeof value === "string" && value.trim()) count++;
    });
    if (searchTerm.trim()) count++;
    return count;
  };

  const transformCaseForExport = (caseData: any) => ({
    'Case Number': caseData.caseNumber || '',
    'Year': caseData.year || '',
    'District': caseData.district || '',
    'Police Station': caseData.policeStation || '',
    'Act Section': caseData.actSection || '',
    'Accused Name': caseData.accusedDetails?.name || '',
    'Father Name': caseData.accusedDetails?.fatherName || '',
    'Age': caseData.accusedDetails?.age || '',
    'Current Stage': getCurrentStage(caseData.stageHistory),
    'Report Date': caseData.reportDate ? new Date(caseData.reportDate).toLocaleDateString("en-IN") : '',
    'Created Date': new Date(caseData.createdAt).toLocaleDateString("en-IN"),
  });

  const exportToExcel = () => {
    const exportData = cases.map(transformCaseForExport);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Goonda Cases');
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `goonda_cases_export_${timestamp}.xlsx`;
    
    XLSX.writeFile(wb, filename);
  };

  useEffect(() => {
    fetchCases();
  }, [filters, itemsPerPage]);

  useEffect(() => {
    if (cases.length > 0) {
      fetchFilterOptions();
    }
  }, [cases]);

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">Goonda Act Cases</h1>
            <p className="text-gray-600 mt-1">Manage preventive action cases under Goonda Act</p>
          </div>
          <Button 
            onClick={() => {
              setEditingCase(null);
              setViewingCase(null);
              setIsFormOpen(true);
            }}
            className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Case
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{total}</div>
                  <p className="text-sm text-gray-600">Total Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {cases.filter((c) => getCurrentStage(c.stages) !== "Completed").length}
                  </div>
                  <p className="text-sm text-gray-600">Active Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {new Set(cases.map((c) => c.policeStation)).size}
                  </div>
                  <p className="text-sm text-gray-600">Police Stations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold text-purple-600">
                    {new Set(cases.map((c) => c.year)).size}
                  </div>
                  <p className="text-sm text-gray-600">Years</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center justify-between">
              Cases List
              <div className="flex items-center gap-2">
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {getActiveFiltersCount()} filters active
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={clearAllFilters} className="text-sm bg-transparent">
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input 
                  placeholder="Search cases..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-10 border-gray-300" 
                />
              </div>
              
              {/* Filters Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-transparent">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 max-h-[600px] overflow-y-auto">
                  <div className="space-y-4">
                    <h4 className="font-semibold">Filter Cases</h4>
                    
                    {/* District Filter */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">District</label>
                      <div className="space-y-2">
                        {filterOptions.districts.map((district) => (
                          <div key={district} className="flex items-center space-x-2">
                            <Checkbox 
                              checked={filters.district.includes(district)} 
                              onCheckedChange={() => handleMultiSelectFilter("district", district)} 
                            />
                            <label className="text-sm">{district}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Police Station Filter */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Police Station</label>
                      <div className="space-y-2">
                        {filterOptions.policeStations.map((station) => (
                          <div key={station} className="flex items-center space-x-2">
                            <Checkbox 
                              checked={filters.policeStation.includes(station)} 
                              onCheckedChange={() => handleMultiSelectFilter("policeStation", station)} 
                            />
                            <label className="text-sm">{station}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Year Filter */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Year</label>
                      <div className="space-y-2">
                        {filterOptions.years.map((year) => (
                          <div key={year} className="flex items-center space-x-2">
                            <Checkbox 
                              checked={filters.year.includes(year)} 
                              onCheckedChange={() => handleMultiSelectFilter("year", year)} 
                            />
                            <label className="text-sm">{year}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Created Date From</label>
                      <Input
                        type="date"
                        max="9999-12-31"
                        value={filters.createdDateFrom}
                        onChange={(e) => handleFilterChange("createdDateFrom", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Created Date To</label>
                      <Input
                        type="date"
                        max="9999-12-31"
                        value={filters.createdDateTo}
                        onChange={(e) => handleFilterChange("createdDateTo", e.target.value)}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] text-white">
                Apply Filters
              </Button>
              
              <Button 
                onClick={exportToExcel} 
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>

            {/* Cases Table */}
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-black">
                    <th className="text-left p-3">Chalani No</th>
                    <th className="text-left p-3">Year</th>
                    <th className="text-left p-3">Goonda Name</th>
                    <th className="text-left p-3">Goonda Type</th>
                    <th className="text-left p-3">District</th>
                    <th className="text-left p-3">Police Station</th>
                    <th className="text-left p-3">Act Section</th>
                    <th className="text-left p-3">Current Stage</th>
                    <th className="text-left p-3">PS Panjikaran Date</th>
                    <th className="text-left p-3 w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((caseData) => (
                    <tr key={caseData.id} className="border-b border-gray-200 hover:bg-gray-50 text-xs text-gray-600">
                      <td className="p-3 font-medium">{caseData.caseNumber}</td>
                      <td className="p-3">{caseData.year}</td>
                      <td className="p-3">{caseData.accusedName}</td>
                      <td className="p-3">{caseData.goondaType}</td>
                      <td className="p-3">{caseData.district}</td>
                      <td className="p-3">{caseData.policeStation}</td>
                      <td className="p-3">{caseData.actSection}</td>
                      <td className="p-3">{getStageBadge(caseData.stages)}</td>
                      <td className="p-3">
                        {new Date(caseData.createdAt || '').toLocaleDateString("en-IN")}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="hover:bg-gray-100 cursor-pointer p-2" 
                            onClick={() => handleViewCase(caseData)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="hover:bg-blue-100 cursor-pointer p-2" 
                            onClick={() => handleEditCase(caseData)}
                          >
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="hover:bg-red-100 cursor-pointer p-2" 
                            onClick={() => setDeleteCase(caseData)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {cases.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No cases found matching your criteria.
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, total)} of {total} cases
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className="border-gray-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <Button 
                            variant={currentPage === page ? "default" : "outline"} 
                            size="sm" 
                            onClick={() => handlePageChange(page)} 
                            className={currentPage === page ? "bg-black text-white" : "border-gray-300"}
                          >
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                    className="border-gray-300"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Case Form Popup */}
      <CriminalFormPopup 
        open={isFormOpen} 
        mode={mode}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) {
            setEditingCase(null);
            setViewingCase(null);
          }
        }} 
        data={editingCase || viewingCase || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCase} onOpenChange={() => setDeleteCase(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Delete Case</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete case "{deleteCase?.caseNumber}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}